import { VITE_SOLANA_VALIDATOR_ADDRESS } from '@/env'
import { useConnection } from '@solana/wallet-adapter-react'
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { fetchValidatorInfo } from '../api/staking'

export type ValidatorAPY = {
  apy: number
  commission: number
  totalStakeLamports: number
  epoch: number | null
  isLoading: boolean
}

const initialState: ValidatorAPY = {
  apy: 0,
  commission: 0,
  totalStakeLamports: 0,
  epoch: null,
  isLoading: true
}

const ValidatorAPYContext = createContext<ValidatorAPY | null>(null)

type ValidatorAPYProviderProps = {
  children: ReactNode
}

export const ValidatorAPYProvider = ({ children }: ValidatorAPYProviderProps) => {
  const { connection } = useConnection()

  const [validatorAPY, setValidatorAPY] = useState<ValidatorAPY>(initialState)

  useEffect(() => {
    const fetchValidatorAPY = async () => {
      setValidatorAPY((prev) => ({ ...prev, isLoading: true }))

      try {
        const data = await fetchValidatorInfo()
        if (data.votePubkey === VITE_SOLANA_VALIDATOR_ADDRESS) {
          const apy = Number.parseFloat(data.apyPercent) || 0
          const commission = Number.parseInt(data.commission, 10) || 0
          const totalStakeLamports = Number(data.totalStakeLamports) || 0
          const epoch = data.epoch ? Number.parseInt(data.epoch, 10) : null
          setValidatorAPY({
            apy: Number(apy.toFixed(2)),
            commission,
            totalStakeLamports,
            epoch,
            isLoading: false
          })
          return
        }
      } catch {
        // Fall through to RPC
      }
    }

    fetchValidatorAPY()
  }, [connection])

  const value = useMemo(() => validatorAPY, [validatorAPY])

  return <ValidatorAPYContext.Provider value={value}>{children}</ValidatorAPYContext.Provider>
}

export const useValidatorAPYContext = (): ValidatorAPY => {
  const context = useContext(ValidatorAPYContext)
  if (context === null) {
    throw new Error('useValidatorAPYContext must be used within ValidatorAPYProvider')
  }
  return context
}
